<template>
  <div class="sidebar__rail">
    <ul class="sidebar__rail-list">
      <li
        v-for="application in railApplications"
        :key="application.id"
        class="sidebar__rail-item"
      >
        <button
          v-tooltip="railLabel(application)"
          type="button"
          class="sidebar__rail-link"
          :class="{ 'sidebar__rail-link--active': isSelected(application) }"
          :aria-label="railLabel(application)"
          :aria-current="isSelected(application) ? 'true' : undefined"
          @click="$emit('selected', application)"
        >
          <i
            class="sidebar__rail-icon"
            :class="application._?.type?.iconClass"
            aria-hidden="true"
          ></i>
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
/**
 * The left sidebar's collapsed state.
 *
 * Collapsing used to `v-show` every section off, leaving a 52px column holding
 * nothing but the workspace avatar and the footer. That is dead space the user
 * is still paying for: the views rail beside it keeps rendering its rows as
 * icons when collapsed, so the two adjacent rails behaved differently for no
 * reason a user could infer, and collapsing the sidebar meant losing the
 * ability to move between applications entirely.
 *
 * This renders the same applications as the expanded tree, as icons. The name
 * moves into a tooltip and the `aria-label`, which is the pattern the views
 * rail already uses (`:name-tooltip="collapsed"` on `ViewsContextItem`).
 *
 * Deliberately NOT rendered here: the per-application options menu, the tables
 * nested under a database, and rename. Those all need width the rail does not
 * have, and unlike the hover-revealed controls in the expanded tree they are
 * not offered at all rather than hidden -- so there is no focus state missing
 * from the rail. Expanding is what makes them available.
 */
export default {
  name: 'SidebarCollapsedRail',
  props: {
    applications: {
      type: Array,
      required: true,
    },
    selectedWorkspace: {
      type: Object,
      required: true,
    },
  },
  emits: ['selected'],
  computed: {
    /**
     * The workspace's applications in the same order the expanded tree shows
     * them: grouped by registered application type, then by `order` within a
     * group. The rail has no headings, so the groups are flattened -- but the
     * order still has to agree with the tree, or expanding the sidebar would
     * reshuffle the list under the user's cursor.
     */
    railApplications() {
      return this.$registry
        .getOrderedList('application')
        .flatMap((applicationType) =>
          this.applications
            .filter(
              (application) =>
                application.workspace.id === this.selectedWorkspace.id &&
                application.type === applicationType.getType() &&
                applicationType.isVisible(application)
            )
            .sort((a, b) => a.order - b.order)
        )
    },
  },
  methods: {
    isSelected(application) {
      return application._?.selected === true
    },
    /**
     * The rail shows no text, so this string is the only name the row has: it
     * is both the tooltip and the accessible name. An application with an empty
     * name would otherwise leave the button announced as nothing at all, which
     * is why the untitled fallback is here rather than in the template.
     */
    railLabel(application) {
      return application.name || this.$t('sidebar.untitledApplication')
    },
  },
}
</script>
